import { Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { AppConstants } from '@core/constants';

interface MetricOption {
  value: number;
  viewValue: string;
  unit: string;
  category: string;
}

interface DialogData {
  availableMetrics: MetricOption[];
  selectedValues: number[];
}

interface CategorizedMetrics {
  [category: string]: MetricOption[];
}

interface GroupedMetrics {
  [key: string]: MetricOption[];
}

@Component({
  selector: 'app-additional-output-metrics-dialog',
  templateUrl: './additional-output-metrics-dialog.component.html',
  styleUrls: ['./additional-output-metrics-dialog.component.scss']
})
export class AdditionalOutputMetricsDialogComponent implements OnInit {
  public categorizedMetrics: CategorizedMetrics = {};
  public selectedMetrics: Set<number> = new Set<number>();
  public readonly maxOutputMetrics: number = AppConstants.TRAINING_ZONE_VALIDATION.MAX_OUTPUT;

  private readonly categoryOrder: string[] = [
    'energy_contribution',
    'substrate_utilization',
    'performance_benchmarks',
    'lactate_heart_rate'
  ];

  private readonly categoryNames: Record<string, string> = {
    energy_contribution: 'Aerobic vs. Anaerobic energy contribution',
    substrate_utilization: 'Substrate utilization (Fat & Carbohydrates)',
    performance_benchmarks: 'Performance Benchmarks',
    lactate: 'Lactate',
    heart_rate: 'Heart Rate'
  };

  private readonly energyContributionOrder: Record<string, number[]> = {
    anaerobic: [
      AppConstants.MASTER_METRIC.ANAEROBIC_POWER_K_CAL,
      AppConstants.MASTER_METRIC.ANAEROBIC_POWER_WATT,
      AppConstants.MASTER_METRIC.ANAEROBIC_POWER_WATT_KG,
      AppConstants.MASTER_METRIC.ANAEROBIC_POWER_WATT_JOULE
    ],
    aerobic: [
      AppConstants.MASTER_METRIC.AEROBIC_POWER_KCAL_H,
      AppConstants.MASTER_METRIC.AEROBIC_POWER_WATT,
      AppConstants.MASTER_METRIC.AEROBIC_POWER_WATT_KG,
      AppConstants.MASTER_METRIC.AEROBIC_POWER_JOULE_H
    ],
    contribution: [
      AppConstants.MASTER_METRIC.ENERGY_CONTRIBUTION_AEROBIC,
      AppConstants.MASTER_METRIC.ENERGY_CONTRIBUTION_ANAEROBIC
    ]
  };

  private readonly substrateUtilizationOrder: Record<string, number[]> = {
    carbohydrates: [
      AppConstants.MASTER_METRIC.CARBOHYDRATES_PERCENT,
      AppConstants.MASTER_METRIC.CARBOHYDRATES_KCAL_H,
      AppConstants.MASTER_METRIC.CARBOHYDRATES_G_H
    ],
    fat: [
      AppConstants.MASTER_METRIC.FAT_PERCENT,
      AppConstants.MASTER_METRIC.FAT_KCAL,
      AppConstants.MASTER_METRIC.FAT_GH
    ],
    total: [
      AppConstants.MASTER_METRIC.TOTAL_ENERGY
    ]
  };

  private readonly performanceBenchmarksOrder: Record<string, number[]> = {
    left: [
      AppConstants.MASTER_METRIC.MAX_LACTATE_CLEARANCE,
      AppConstants.MASTER_METRIC.ANAEROBIC_THRESHOLD,
      AppConstants.MASTER_METRIC.LT1,
      AppConstants.MASTER_METRIC.FAT_MAX,
      AppConstants.MASTER_METRIC.FAT_MAX_POWER
    ],
    right: [
      AppConstants.MASTER_METRIC.VLA_MAX,
      AppConstants.MASTER_METRIC.VO2_MAX,
      AppConstants.MASTER_METRIC.MAX_AEROBIC,
      AppConstants.MASTER_METRIC.ANAEROBIC_RESERVE
    ]
  };

  private readonly lactateOrder: number[] = [
    AppConstants.MASTER_METRIC.MAX_LACTATE_PRODUCTION_RATE,
    AppConstants.MASTER_METRIC.LACTATE_CLEARANCE_RATE,
    AppConstants.MASTER_METRIC.LACTATE_ACCUMULATION_RATE,
    AppConstants.MASTER_METRIC.LACTATE_CONCENTRATION
  ];

  private readonly heartRateOrder: number[] = [
    AppConstants.MASTER_METRIC.MAX_HEART_RATE_PERCENT,
    AppConstants.MASTER_METRIC.HEART_RATE
  ];

  constructor(
    public dialogRef: MatDialogRef<AdditionalOutputMetricsDialogComponent>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}

  public ngOnInit(): void {
    this.initializeSelectedMetrics();
    this.categorizeMetrics();
  }

  private initializeSelectedMetrics(): void {
    if (Array.isArray(this.data.selectedValues)) {
      this.data.selectedValues.forEach((value: number): void => {
        this.selectedMetrics.add(value);
      });
    }
  }

  private categorizeMetrics(): void {
    this.data.availableMetrics.forEach((metric: MetricOption): void => {
      const category: string = metric.category || 'Other';
      if (!this.categorizedMetrics[category]) {
        this.categorizedMetrics[category] = [];
      }
      this.categorizedMetrics[category].push(metric);
    });
  }

  private sortMetricsByOrder(metrics: MetricOption[], order: number[]): MetricOption[] {
    return metrics.sort((a: MetricOption, b: MetricOption): number => {
      const indexA: number = order.indexOf(a.value);
      const indexB: number = order.indexOf(b.value);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  public get categoryKeys(): string[] {
    return this.categoryOrder.filter((key: string): boolean => {
      if (key === 'lactate_heart_rate') {
        return !!(this.categorizedMetrics['lactate'] || this.categorizedMetrics['heart_rate']);
      }
      return !!this.categorizedMetrics[key];
    });
  }

  public getCategoryDisplayName(categoryKey: string): string {
    return this.categoryNames[categoryKey] || categoryKey;
  }

  public getMetricsForCategory(categoryKey: string): MetricOption[] {
    const metrics: MetricOption[] = this.categorizedMetrics[categoryKey] || [];

    if (categoryKey === 'lactate') {
      return this.sortMetricsByOrder(metrics, this.lactateOrder);
    }

    if (categoryKey === 'heart_rate') {
      return this.sortMetricsByOrder(metrics, this.heartRateOrder);
    }

    return metrics;
  }

  public isEnergyContribution(categoryKey: string): boolean {
    return categoryKey === 'energy_contribution';
  }

  public isSubstrateUtilization(categoryKey: string): boolean {
    return categoryKey === 'substrate_utilization';
  }

  public isPerformanceBenchmarks(categoryKey: string): boolean {
    return categoryKey === 'performance_benchmarks';
  }

  public isLactateHeartRate(categoryKey: string): boolean {
    return categoryKey === 'lactate_heart_rate';
  }

  public getEnergyContributionGroups(): GroupedMetrics {
    const metrics: MetricOption[] = this.getMetricsForCategory('energy_contribution');
    const groups: GroupedMetrics = {
      anaerobic: [],
      aerobic: [],
      contribution: []
    };

    metrics.forEach((metric: MetricOption): void => {
      if (this.energyContributionOrder.anaerobic.includes(metric.value)) {
        groups.anaerobic.push(metric);
      } else if (this.energyContributionOrder.aerobic.includes(metric.value)) {
        groups.aerobic.push(metric);
      } else if (this.energyContributionOrder.contribution.includes(metric.value)) {
        groups.contribution.push(metric);
      }
    });

    groups.anaerobic = this.sortMetricsByOrder(groups.anaerobic, this.energyContributionOrder.anaerobic);
    groups.aerobic = this.sortMetricsByOrder(groups.aerobic, this.energyContributionOrder.aerobic);
    groups.contribution = this.sortMetricsByOrder(groups.contribution, this.energyContributionOrder.contribution);

    return groups;
  }

  public getSubstrateUtilizationGroups(): GroupedMetrics {
    const metrics: MetricOption[] = this.getMetricsForCategory('substrate_utilization');
    const groups: GroupedMetrics = {
      carbohydrates: [],
      fat: [],
      total: []
    };

    metrics.forEach((metric: MetricOption): void => {
      if (this.substrateUtilizationOrder.carbohydrates.includes(metric.value)) {
        groups.carbohydrates.push(metric);
      } else if (this.substrateUtilizationOrder.fat.includes(metric.value)) {
        groups.fat.push(metric);
      } else if (this.substrateUtilizationOrder.total.includes(metric.value)) {
        groups.total.push(metric);
      }
    });

    groups.carbohydrates = this.sortMetricsByOrder(groups.carbohydrates, this.substrateUtilizationOrder.carbohydrates);
    groups.fat = this.sortMetricsByOrder(groups.fat, this.substrateUtilizationOrder.fat);
    groups.total = this.sortMetricsByOrder(groups.total, this.substrateUtilizationOrder.total);

    return groups;
  }

  public getPerformanceBenchmarksColumns(): { left: MetricOption[]; right: MetricOption[] } {
    const metrics: MetricOption[] = this.getMetricsForCategory('performance_benchmarks');
    const left: MetricOption[] = [];
    const right: MetricOption[] = [];

    metrics.forEach((metric: MetricOption): void => {
      if (this.performanceBenchmarksOrder.left.includes(metric.value)) {
        left.push(metric);
      } else if (this.performanceBenchmarksOrder.right.includes(metric.value)) {
        right.push(metric);
      }
    });

    return {
      left: this.sortMetricsByOrder(left, this.performanceBenchmarksOrder.left),
      right: this.sortMetricsByOrder(right, this.performanceBenchmarksOrder.right)
    };
  }

  public get selectedCount(): number {
    return this.selectedMetrics.size;
  }

  public isMetricSelected(metricValue: number): boolean {
    return this.selectedMetrics.has(metricValue);
  }

  public isMetricDisabled(metricValue: number): boolean {
    return !this.isMetricSelected(metricValue) && this.selectedMetrics.size >= this.maxOutputMetrics;
  }

  public onMetricToggle(metricValue: number): void {
    if (this.selectedMetrics.has(metricValue)) {
      this.selectedMetrics.delete(metricValue);
      return;
    }

    if (this.selectedMetrics.size >= this.maxOutputMetrics) {
      this.snackBar.open(
        `Maximum output value is ${this.maxOutputMetrics}`,
        'OK',
        AppConstants.TOAST_CONFIG.ERROR
      );
      return;
    }

    this.selectedMetrics.add(metricValue);
  }

  public onDeselectAll(): void {
    this.selectedMetrics.clear();
  }

  public onCancel(): void {
    this.dialogRef.close(null);
  }

  public onApply(): void {
    this.dialogRef.close(Array.from(this.selectedMetrics));
  }
}
